import React, { useState, useRef } from 'react';
import {
  Typography, Button, Alert, TextField, InputAdornment, Box
} from '@mui/material';
import axios from 'axios';
import useMessages from '../../../hooks/useMessages';
import FormInputMUI from '../../commun/FormInputMUI';
import DisponibilitePicker from '../../commun/DisponibilitePicker';
import FormLayout from '../../commun/FormLayout';
import Footer from '../../commun/Footer';
import { CurrencySelect } from '../../Recruteur/candidat/AddTalent/CurrencySelect';
import { contractTypeOptions } from '../../commun/constants/options';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './signup.css';

// Icônes
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PhoneInput from 'react-phone-input-2';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const FormCandidat = () => {
  const [formData, setFormData] = useState({
    lastName: '', firstName: '', email: '', phone: '',
    cv: null, linkedin: '', remarks: '',
    typesContrat: [], remunerations: {}, devises: {}, disponibilite: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  const { messages, handleSuccess, handleError } = useMessages();

  const setField = (name, value) =>
    setFormData(prev => ({ ...prev, [name]: value }));

  const handleFileChange = e => {
    if (e.target.files[0]) setField('cv', e.target.files[0]);
  };

  const handleContractTypeChange = (selectedTypes) => {
    const selectedArray = Array.isArray(selectedTypes) ? selectedTypes : [];
    setFormData(prev => ({
      ...prev,
      typesContrat: selectedArray,
      remunerations: Object.fromEntries(
        selectedArray.map(({ value }) => [value, prev.remunerations[value] || ''])
      ),
      devises: Object.fromEntries(
        selectedArray.map(({ value }) => [value, prev.devises[value] || '€'])
      )
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.lastName) newErrors.lastName = 'Champ requis.';
    if (!formData.firstName) newErrors.firstName = 'Champ requis.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Email invalide.';
    if (!/^\+?[0-9]{10,15}$/.test(formData.phone)) newErrors.phone = 'Téléphone invalide.';
    if (!formData.cv) newErrors.cv = 'CV requis.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    const data = new FormData();
    for (const [key, value] of Object.entries(formData)) {
      if (key === 'remunerations' || key === 'devises') {
        data.append(key, JSON.stringify(value));
      } else if (key === 'typesContrat') {
        value.forEach(val => data.append(`${key}[]`, val.value));
      } else {
        data.append(key, value);
      }
    }

    try {
      await axios.post(`${API_BASE}/api/auth/signup-candidat`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      handleSuccess('Votre candidature a été enregistrée.');
      setFormData({
        lastName: '', firstName: '', email: '', phone: '',
        cv: null, linkedin: '', remarks: '', typesContrat: [],
        remunerations: {}, devises: {}, disponibilite: '',
      });
      setErrors({});
      fileInputRef.current.value = null;
    } catch (err) {
      handleError(err.response?.data?.message || 'Erreur lors de l’envoi.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FormLayout
        infoTitle="Déposez votre CV, on s’occupe du reste"
        infoSubtitle="Notre équipe commerciale recherche les meilleures opportunités pour vous"
        infoPoints={[
          "✓ Intégrez notre vivier de talents IT",
          "✓ Soyez contacté dès qu’une mission vous correspond",
          "✓ Bénéficiez d’un accompagnement personnalisé"
        ]}
      >
        <Typography variant="h4" align="center" gutterBottom>
          Formulaire Candidat
        </Typography>

        {messages.error && <Alert severity="error">{messages.error}</Alert>}
        {messages.success && <Alert severity="success">{messages.success}</Alert>}

        <form onSubmit={handleSubmit}>
          <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
            <FormInputMUI
              label="Nom"
              required
              value={formData.lastName}
              onChange={val => setField('lastName', val)}
              error={errors.lastName}
              icon={<PersonIcon fontSize="small" />}
            />
            <FormInputMUI
              label="Prénom"
              required
              value={formData.firstName}
              onChange={val => setField('firstName', val)}
              error={errors.firstName}
              icon={<PersonIcon fontSize="small" />}
            />
            <FormInputMUI
              label="Email"
              required
              type="email"
              value={formData.email}
              onChange={val => setField('email', val)}
              error={errors.email}
              icon={<EmailIcon fontSize="small" />}
            />
              <Box>
                <PhoneInput
                  country="fr"
                  onlyCountries={['fr', 'ma', 'tn']}
                  value={formData.phone}
                  onChange={(value) => {
                    const fullNumber = `+${value}`;
                    setField('phone', fullNumber);
                  }}
                  inputStyle={{ width: '100%', height: 50 }}
                />

                {(errors.phone) && (
                  <p style={{ color: 'red' }}>{errors.phone}</p>
                )}
              </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} gridColumn="span 2">
            <DisponibilitePicker
              label="Disponibilité"
              required
              value={formData.disponibilite}
              onChange={(val) => setField('disponibilite', val)}
              error={!!errors.disponibilite}
              helperText={errors.disponibilite}
            />

            <FormInputMUI
              type="chip-select"
              label="Types de contrat"
              required
              value={formData.typesContrat}
              options={contractTypeOptions}
              onChange={handleContractTypeChange}
              name="typesContrat"
            />
          </Box>


            {/* Rémunérations */}
            {formData.typesContrat.map(({ value }) => (
              <Box key={value} gridColumn="span 2">
                <TextField
                  label={`Tarif souhaité pour ${value}`}
                  required
                  fullWidth
                  type="number"
                  value={formData.remunerations[value] || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setFormData(prev => ({
                        ...prev,
                        remunerations: {
                          ...prev.remunerations,
                          [value]: val,
                        }
                      }));
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CurrencySelect
                          value={formData.devises[value] || '€'}
                          onChange={(currency) => {
                            setFormData(prev => ({
                              ...prev,
                              devises: {
                                ...prev.devises,
                                [value]: currency
                              }
                            }));
                          }}
                        />
                      </InputAdornment>
                    ),
                    inputProps: { min: 0 }
                  }}
                />
              </Box>
            ))}

            {/* CV */}
            <Box gridColumn="span 2">
              <Button
                variant="outlined"
                onClick={() => fileInputRef.current.click()}
                startIcon={<UploadFileIcon />}
                className="cv-button"
              >
                {formData.cv ? formData.cv.name : 'Déposer un CV'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                hidden
                onChange={handleFileChange}
              />
              {errors.cv && (
                <Typography color="error" variant="caption">{errors.cv}</Typography>
              )}
            </Box>

            {/* LinkedIn + Remarques */}
            <Box gridColumn="span 2">
              <FormInputMUI
                label="Profil LinkedIn"
                value={formData.linkedin}
                onChange={val => setField('linkedin', val)}
                icon={<LinkedInIcon fontSize="small" />}
              />
            </Box>
            <Box gridColumn="span 2">
              <FormInputMUI
  label="Informations complémentaires"
  minRows={0.4}
  maxRows={0.4}
  value={formData.remarks}
  onChange={val => setField('remarks', val)}
  icon={<InfoOutlinedIcon fontSize="small" />}
/>

            </Box>

            <Box gridColumn="span 2">
              <Button
                type="submit"
                variant="contained"
                fullWidth
                className="formc-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Envoi...' : 'Envoyer'}
              </Button>
            </Box>
          </Box>
        </form>
      </FormLayout>
      <Footer />
    </>
  );
};

export default FormCandidat;
